import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

export default function SummaryList({ summaries }) {
  return (
    <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
      {Object.entries(summaries).map(([cat, { content, sources }]) => (
        <AccordionItem
          key={cat}
          title={cat}
          content={content}
          sources={sources}
        />
      ))}
    </div>
  );
}

function AccordionItem({ title, content, sources }) {
  const [isOpen, setIsOpen] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);

  const handleSpeak = (e) => {
    e.stopPropagation();
    const synth = window.speechSynthesis;
    if (!synth) return;

    if (isSpeaking) {
      synth.cancel();
      setIsSpeaking(false);
      return;
    }

    let text = content
      .replace(/\[(.*?)\]\(.*?\)/g, '$1')
      .replace(/https?:\/\/[\w\-\.\/\?\=%&]+/g, '')
      .replace(/\*+/g, '')
      .replace(/`+/g, '')
      .replace(/#{1,6}\s+/g, '')
      .replace(/>\s+/g, '')
      .trim();

    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = 'en-US';
    utterance.rate = 1.0;

    const voices = synth.getVoices();
    const germanVoices = voices.filter(v => v.lang.startsWith('en'));
    if (germanVoices.length > 0) {
      utterance.voice = germanVoices[0];
    }

    utterance.onstart = () => setIsSpeaking(true);
    utterance.onend = () => setIsSpeaking(false);
    utterance.onerror = () => setIsSpeaking(false);

    synth.cancel();
    synth.speak(utterance);
  };

    // Custom link renderer for markdown
    const LinkRenderer = ({ href, children }) => {
      // Strip query parameters (e.g., ?utm_source=openai)
      const cleanHref = href.split('?')[0];
      return (
        <a
          href={cleanHref}
          target="_blank"
          rel="noopener noreferrer"
          referrerPolicy="no-referrer"
        >
          {children}
        </a>
      );
    };

  return (
    <div className="border rounded-lg shadow-sm bg-white dark:bg-gray-800 overflow-hidden">
      <div className="relative">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="group w-full flex items-center justify-between p-4 focus:outline-none"
        >
          <div className="flex items-center space-x-2">
            <span
              className={`inline-block transform transition-transform duration-200 text-2xl text-gray-600 dark:text-gray-300 ${isOpen ? 'rotate-0' : '-rotate-90'}`}
            >
              ▾
            </span>
            <h2
              className={`text-xl font-semibold capitalize text-gray-900 dark:text-gray-100 transform transition-transform duration-200 ${!isOpen ? 'group-hover:scale-105' : ''}`}
            >
              {title}
            </h2>
          </div>
        </button>
        <button
          onClick={handleSpeak}
          className="absolute top-4 right-4 text-xl text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          aria-label={isSpeaking ? 'Stop reading' : 'Read aloud'}
        >
          {isSpeaking ? '🔇' : '🔊'}
        </button>
      </div>

      {isOpen && (
        <div className="px-4 pb-4">
          <div className="prose prose-lg dark:prose-invert text-gray-800 dark:text-gray-200">
            <ReactMarkdown remarkPlugins={[remarkGfm]}
              components={{
                a: LinkRenderer
              }}
              transformLinkUri={(href) => href.split('?')[0]}
            > 
              {content}
            </ReactMarkdown>
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            {sources.map(src => (
              <a href={src.url} target="_blank" rel="noopener noreferrer" referrerPolicy="no-referrer" className="text-sm bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-full px-3 py-1 hover:bg-gray-300 dark:hover:bg-gray-600 transition">
                {src.domain}
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
